import { Card } from "@cmsgov/ds-medicare-gov";
import React, { FunctionComponent, useContext } from "react";
import { PapPlan, UserLanguage } from "../../@types";
import { AnalyticsActionType } from "../../app/contexts/Analytics/types";
import { AppStore } from "../../app/store";
import { useTranslate } from "../../helpers";

interface PapPlanCardProps {
  papPlan: PapPlan;
  drugName: string;
}

const PapPlanCard: FunctionComponent<PapPlanCardProps> = ({
  papPlan,
  drugName,
}) => {
  const t = useTranslate();
  const {
    state: { language },
    dispatch,
  } = useContext(AppStore.AppContext);

  return (
    <Card className="PapPlanCard ds-u-padding--2 ds-u-margin-top--3">
      <div className="ds-l-row">
        <div className="PapPlanCard__cardColumn ds-l-col--12 ds-l-md-col--6 ds-u-border-right--1">
          <h2 id={papPlan.program_name}>
            {t("pap.plans.drug_program")}{" "}
            <span className="PapPlanCard__programName">
              {papPlan.program_name}
            </span>
          </h2>
          <div className="ds-u-color--primary ds-text-heading--xl ds-u-margin--0">
            {drugName}
          </div>
          <h3 className="PapPlanCard__sectionHeading">
            {t("pap.plans.contact_information")}
          </h3>
          <div className="ds-l-row PapPlanCard__contact">
            {papPlan.address1 && (
              <>
                <div className="ds-u-font-weight--semibold ds-l-col--3">
                  {t("pap.plans.address")}{" "}
                </div>
                <div className="ds-l-col--9">
                  <span>{papPlan.address1}</span>
                  {papPlan.address2 && (
                    <>
                      <br />
                      <span>{papPlan.address2}</span>
                    </>
                  )}{" "}
                  <br />{" "}
                  <span>
                    {papPlan.city}, {papPlan.state} {papPlan.zip}
                  </span>
                </div>
              </>
            )}
            {papPlan.phone1 && (
              <>
                <div className="ds-u-font-weight--semibold ds-l-col--3">
                  {t("pap.plans.phone")}{" "}
                </div>
                <div className="ds-l-col--9">
                  <a
                    href={`tel:${papPlan.phone1}`}
                    onClick={() => {
                      dispatch({
                        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                        settings: {
                          event_action: "Phone Click",
                          event_label: `${drugName}: ${papPlan.program_name} ${papPlan.phone1}`,
                          other_props: { event_category: "PAP" },
                        },
                      });
                    }}
                  >
                    {papPlan.phone1}
                  </a>
                </div>
              </>
            )}
          </div>
          {papPlan.url && (
            <>
              <a
                href={papPlan.url}
                aria-describedby={papPlan.program_name}
                target="_blank"
                rel="noopener noreferrer"
                className="mct-hide-print ds-u-margin-top--2 ds-c-button"
                onClick={() => {
                  dispatch({
                    type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                    settings: {
                      event_action: "External Link Click",
                      event_label: `View program website: ${papPlan.url}`,
                      other_props: { event_category: "PAP" },
                    },
                  });
                }}
              >
                {t("pap.plans.view_program_website")}
              </a>
              <div className="mct-print-only ds-u-margin-top--2">
                {papPlan.url}
              </div>
            </>
          )}
        </div>
        <div className="PapPlanCard__cardColumn ds-u-margin-top--2 ds-u-md-margin-top--0 ds-l-col--12 ds-l-md-col--6">
          <div className="ds-u-padding-left--0 ds-u-md-padding-left--1">
            <h3 className="PapPlanCard__sectionHeading">
              {t("pap.plans.eligibility_criteria")}
            </h3>
            <ul className="ds-c-list mct-disc-list" role="list">
              {(language === UserLanguage.SPANISH &&
              papPlan.eligibility_spanish.length
                ? papPlan.eligibility_spanish
                : papPlan.eligibility
              ).map((criterion, i) => {
                return (
                  <li
                    key={i}
                    role="listitem"
                    dangerouslySetInnerHTML={{
                      __html: criterion.replace(/<br>/g, " "),
                    }}
                  />
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PapPlanCard;
